import { Outlet } from "react-router-dom";
import Icon from "@/components/ui/Icon.jsx";
import { useTheme } from "@/context/theme.js";

/*
 * Replaces frontend/app/components/layout/AuthLayout.js.
 * Ported from the brand panel that login.html and register.html shared —
 * identical in both, which is why it is one component here.
 *
 * The panel is hidden below the breakpoint and a compact brand pill appears
 * above the form instead. `children`/<Outlet/> is the form card.
 *
 * `stats` lets a page swap what the glass cards show: login shows live-looking
 * product numbers, register shows the three setup steps.
 */
export default function AuthLayout() {
    const { theme, toggleTheme } = useTheme();

    return (
        <div className="flex min-h-full">
            {/* Brand panel — md and up */}
            <aside className="relative hidden overflow-hidden bg-brand-700 md:flex md:w-[42%] lg:w-[46%] xl:w-1/2">
                {/* Mesh gradient */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,#818cf8_0%,transparent_45%),radial-gradient(circle_at_82%_82%,#c084fc_0%,transparent_50%),linear-gradient(135deg,#4338ca_0%,#6d28d9_100%)]" />

                {/* Dot grid, faded toward the bottom-right */}
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.18)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:linear-gradient(to_bottom_right,rgba(0,0,0,0.45),transparent_70%)]" />

                <div className="relative flex w-full flex-col justify-between p-10 lg:p-14">
                    <div className="flex animate-fade-up items-center gap-3">
                        <span className="grid size-9 place-items-center rounded-xl border border-white/25 bg-white/15 text-white">
                            <Icon name="clock" className="size-4.5" />
                        </span>
                        <span className="text-[17px] font-semibold tracking-tight text-white">
                            Performance Track
                        </span>
                    </div>

                    <div className="max-w-lg">
                        <h1 className="animate-fade-up text-[clamp(30px,4vw,44px)] leading-[1.08] font-bold tracking-tight text-white">
                            Every minute,{" "}
                            <span className="bg-gradient-to-r from-white to-violet-200 bg-clip-text text-transparent">
                                accounted for.
                            </span>
                        </h1>
                        <p className="mt-4 animate-fade-up text-[15px] leading-relaxed text-white/75 [animation-delay:80ms]">
                            Activity, screenshots and productivity insights — stitched into one clean
                            dashboard your team actually opens.
                        </p>

                        <div className="mt-9 grid grid-cols-3 gap-3">
                            <GlassStat
                                icon="clock"
                                label="Tracked today"
                                value="8h 32m"
                                delay="0ms"
                                footer={
                                    <div className="mt-2.5 flex h-5 items-end gap-0.5">
                                        {[38, 62, 80, 55, 92, 100].map((h, i) => (
                                            <span
                                                key={i}
                                                className={`w-full rounded-sm ${i === 5 ? "bg-white/70" : "bg-white/35"}`}
                                                style={{ height: `${h}%` }}
                                            />
                                        ))}
                                    </div>
                                }
                            />
                            <GlassStat
                                icon="camera"
                                label="Captures"
                                value="128"
                                delay="900ms"
                                footer={
                                    <p className="mt-2.5 text-[11px] text-white/60">every 5 minutes</p>
                                }
                            />
                            <GlassStat
                                icon="chart"
                                label="Productive"
                                value="94%"
                                delay="1800ms"
                                footer={
                                    <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-white/20">
                                        <span className="block h-full w-[94%] rounded-full bg-white/80" />
                                    </div>
                                }
                            />
                        </div>
                    </div>

                    <div className="flex animate-fade-up items-center justify-between text-[12px] text-white/60 [animation-delay:400ms]">
                        <span>© {new Date().getFullYear()} HasTech IT</span>
                        <span className="flex items-center gap-1.5">
                            <Icon name="team" className="size-3.5" />
                            Trusted by remote-first teams
                        </span>
                    </div>
                </div>
            </aside>

            {/* Form panel */}
            <main className="relative flex min-w-0 flex-1 items-center justify-center p-5 sm:p-8">
                <button
                    type="button"
                    onClick={toggleTheme}
                    aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
                    className="absolute top-5 right-5 grid size-9 place-items-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                    <Icon name={theme === "dark" ? "sun" : "moon"} className="size-5" />
                </button>

                <Outlet />
            </main>
        </div>
    );
}

function GlassStat({ icon, label, value, delay, footer }) {
    return (
        <div className="glass animate-float" style={{ animationDelay: delay }}>
            <p className="flex items-center gap-1.5 text-[11.5px] text-white/75">
                <Icon name={icon} className="size-3.5" />
                {label}
            </p>
            <p className="mt-1 text-[22px] leading-none font-semibold tracking-tight">{value}</p>
            {footer}
        </div>
    );
}

/**
 * Compact brand pill for the form panel below the md breakpoint, where the brand
 * panel is hidden. Login and Register both render this above their card.
 */
export function AuthBrandPill() {
    return (
        <div className="mb-7 flex justify-center md:hidden">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1.5 text-[13px] font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-200">
                <Icon name="clock" className="size-4" />
                Performance Track
            </span>
        </div>
    );
}
